import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, Printer, RotateCcw, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BADGE_URL = '/images/badge-transparent.png';

/* ─── Colorado Flag SVG (background watermark) ─── */
const ColoradoFlag = ({ opacity = 0.06 }) => (
  <svg viewBox="0 0 300 200" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity, pointerEvents: 'none' }}>
    {/* Blue stripes */}
    <rect x="0" y="0" width="300" height="66.67" fill="#002868" />
    <rect x="0" y="133.33" width="300" height="66.67" fill="#002868" />
    {/* White stripe */}
    <rect x="0" y="66.67" width="300" height="66.67" fill="#FFFFFF" />
    {/* Red C */}
    <circle cx="120" cy="100" r="50" fill="none" stroke="#C8102E" strokeWidth="24" />
    {/* Gold circle */}
    <circle cx="120" cy="100" r="18" fill="#FFD700" />
  </svg>
);

/* ─── Small Colorado C icon ─── */
const ColoradoC = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#002868" />
    <path d="M 30 50 A 25 25 0 1 1 50 75" stroke="#C8102E" strokeWidth="18" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="50" r="14" fill="#FFD700" />
  </svg>
);

/* ─── Helpers ─── */
function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

function getExpirationDate(issueDate) {
  if (!issueDate) return '—';
  try {
    const d = new Date(issueDate);
    d.setFullYear(d.getFullYear() + 2);
    return formatDate(d.toISOString());
  } catch { return '—'; }
}

const CARD_W = 280;
const CARD_H = 440;

/* ═══════════════ FRONT ═══════════════ */
const CardFront = ({ data }) => (
  <div
    data-testid="id-card-front"
    className="relative overflow-hidden bg-white"
    style={{ width: CARD_W, height: CARD_H, borderRadius: 12, border: '2px solid #d1d5db', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
  >
    {/* Colorado flag watermark behind everything */}
    <ColoradoFlag opacity={0.045} />

    {/* Preview watermark */}
    {data.preview && (
      <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
        <span className="text-[36px] font-black text-red-500/15 -rotate-[20deg] tracking-widest select-none">PREVIEW</span>
      </div>
    )}

    {/* Lanyard slot */}
    <div className="flex justify-center" style={{ paddingTop: 6 }}>
      <div style={{ width: 36, height: 10, borderRadius: 5, background: '#e5e7eb', border: '1px solid #d1d5db' }} />
    </div>

    {/* Header bar */}
    <div className="mx-3 mt-2 text-center" style={{ background: '#111', borderRadius: 4, padding: '10px 8px 12px' }}>
      <p style={{ fontSize: 11, letterSpacing: '0.15em', color: '#fff', fontWeight: 600, lineHeight: 1.3 }}>
        BAIL ENFORCEMENT
      </p>
      <p style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: '0.06em', lineHeight: 1.1, fontFamily: 'Barlow Condensed, Arial Black, sans-serif' }}>
        AGENT
      </p>
    </div>

    {/* Badge + Photo row */}
    <div className="flex items-center justify-center gap-4 px-5" style={{ marginTop: 12 }}>
      {/* Badge */}
      <img
        src={BADGE_URL}
        alt="BEA Badge"
        className="object-cover"
        style={{ width: 100, height: 100, borderRadius: '50%' }}
        referrerPolicy="no-referrer"
      />

      {/* Photo placeholder or actual photo */}
      {data.photo ? (
        <img
          src={data.photo}
          alt="Agent"
          className="object-cover border-2 border-gray-300"
          style={{ width: 96, height: 112, borderRadius: 4, flexShrink: 0 }}
        />
      ) : (
        <div
          className="flex items-center justify-center bg-gray-100 border-2 border-gray-300"
          style={{ width: 96, height: 112, borderRadius: 4, flexShrink: 0 }}
        >
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
          </svg>
        </div>
      )}
    </div>

    {/* Info fields */}
    <div className="px-5" style={{ marginTop: 16 }}>
      <InfoRow label="AGENT ID" value={data.certificate_number} bold />
      <InfoRow label="ISSUED" value={formatDate(data.issue_date)} />
      <InfoRow label="EXP. DATE" value={getExpirationDate(data.issue_date)} />
    </div>

    {/* Agent name */}
    <div className="px-5" style={{ marginTop: 14 }}>
      <p style={{ fontSize: 17, fontWeight: 800, color: '#111', letterSpacing: '0.02em', lineHeight: 1.2, fontFamily: 'Barlow Condensed, Arial Black, sans-serif' }}>
        {data.full_name?.toUpperCase()}
      </p>
    </div>

    {/* Duty notice + Colorado State Seal */}
    <div className="px-5" style={{ marginTop: 10 }}>
      <p style={{ fontSize: 7, color: '#6b7280', lineHeight: 1.5, letterSpacing: '0.01em' }}>
        This identification must be presented upon request
        while performing bail enforcement duties.
      </p>
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 6.5, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Verify at</p>
          <p style={{ fontSize: 8, fontWeight: 600, color: '#374151' }}>coloradobea.org</p>
        </div>
        <img
          src="/images/colorado-seal.png"
          alt="State of Colorado Seal"
          style={{ width: 52, height: 52, borderRadius: '50%' }}
        />
      </div>
    </div>

    {/* Bottom bar */}
    <div
      className="absolute bottom-0 left-0 right-0 text-center"
      style={{ background: '#002868', padding: '5px 0' }}
    >
      <p style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.8)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
        Colorado Bail Enforcement Agency LLC
      </p>
    </div>
  </div>
);

/* ═══════════════ BACK ═══════════════ */
const CardBack = ({ data }) => (
  <div
    data-testid="id-card-back"
    className="relative overflow-hidden bg-white"
    style={{ width: CARD_W, height: CARD_H, borderRadius: 12, border: '2px solid #d1d5db', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
  >
    {/* Colorado flag watermark */}
    <ColoradoFlag opacity={0.035} />

    {/* Top accent bar */}
    <div style={{ height: 5, background: '#002868' }} />

    <div className="px-4 pt-3 relative z-10">
      {/* Licensing statement */}
      <p style={{ fontSize: 8.5, lineHeight: 1.55, color: '#374151' }}>
        The individual identified on this card has successfully completed the
        Colorado Bail Enforcement Certification Course and is licensed to
        operate as a bail enforcement agent pursuant to{' '}
        <span style={{ fontWeight: 700 }}>C.R.S. § 10-2-301</span> and{' '}
        <span style={{ fontWeight: 700 }}>C.R.S. § 12-7-105.5</span>.
      </p>

      {/* Taylor v. Taintor */}
      <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: 8, lineHeight: 1.5, color: '#6b7280', fontStyle: 'italic' }}>
          This agent operates under the authority recognized in{' '}
          <span style={{ fontWeight: 600, fontStyle: 'normal' }}>Taylor v. Taintor</span>,
          83 U.S. (16 Wall.) 366 (1872) — establishing the contractual right
          of sureties to apprehend and surrender principals who have failed
          to appear.
        </p>
      </div>

      {/* DOI Complaints Section */}
      <div style={{ marginTop: 10, padding: '8px 8px', background: '#f9fafb', borderRadius: 4, border: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: 7.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
          Complaints & Concerns
        </p>
        <p style={{ fontSize: 7.5, lineHeight: 1.55, color: '#6b7280' }}>
          All complaints and/or concerns regarding this agent should
          be directed to:
        </p>
        <div style={{ marginTop: 4 }}>
          <p style={{ fontSize: 8, fontWeight: 700, color: '#111' }}>
            Colorado Division of Insurance
          </p>
          <p style={{ fontSize: 7.5, color: '#374151', lineHeight: 1.5 }}>
            1560 Broadway, Suite 850<br />
            Denver, CO 80202
          </p>
          <p style={{ fontSize: 7.5, color: '#374151', marginTop: 2 }}>
            <span style={{ fontWeight: 600 }}>Phone:</span> 303-894-7490
          </p>
          <p style={{ fontSize: 7.5, color: '#374151' }}>
            <span style={{ fontWeight: 600 }}>Toll-Free:</span> 1-800-930-3745
          </p>
          <p style={{ fontSize: 7.5, color: '#374151' }}>
            <span style={{ fontWeight: 600 }}>Web:</span> doi.colorado.gov
          </p>
        </div>
      </div>

      {/* Agent ID + Colorado C */}
      <div className="flex items-end justify-between" style={{ marginTop: 8 }}>
        <div>
          <p style={{ fontSize: 6.5, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Agent ID</p>
          <p style={{ fontSize: 9, fontWeight: 700, color: '#374151', fontFamily: 'monospace' }}>{data.certificate_number}</p>
        </div>
        <div className="flex flex-col items-center">
          <p style={{ fontSize: 6, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 2 }}>
            Scan to Verify
          </p>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(window.location.origin + '/verify?id=' + (data.certificate_number || ''))}&margin=0`}
            alt="Verify QR"
            style={{ width: 44, height: 44 }}
            crossOrigin="anonymous"
          />
        </div>
      </div>
    </div>

    {/* Bottom accent */}
    <div className="absolute bottom-0 left-0 right-0" style={{ height: 5, background: '#C8102E' }} />
  </div>
);

/* ─── Info row helper ─── */
const InfoRow = ({ label, value, bold }) => (
  <div className="flex items-baseline gap-2" style={{ marginBottom: 4 }}>
    <span style={{ fontSize: 8, color: '#9ca3af', letterSpacing: '0.12em', textTransform: 'uppercase', width: 60, flexShrink: 0, fontWeight: 600 }}>
      {label}
    </span>
    <span style={{ fontSize: 11, fontWeight: bold ? 700 : 600, color: bold ? '#111' : '#374151', fontFamily: bold ? 'monospace' : 'inherit' }}>
      {value || '—'}
    </span>
  </div>
);

/* ═════════════ PAGE ═════════════ */
export default function IDCardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/progress/id-card`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Could not load ID card data');
        }
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        const token = sessionStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/auth/upload-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ photo_base64: base64 }),
        });
        if (res.ok) {
          setData(prev => ({ ...prev, photo: base64 }));
          toast.success('Photo uploaded — your ID card is updated');
        } else {
          toast.error('Failed to upload photo');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Upload failed');
      setUploading(false);
    }
  };

  const handleDownload = async (side) => {
    const el = document.querySelector(`[data-testid="id-card-${side}"]`);
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 3, useCORS: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `bea-agent-id-${side}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success(`${side === 'front' ? 'Front' : 'Back'} downloaded`);
    } catch {
      toast.error('Download failed — try right-clicking the card and saving the image');
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading Agent ID...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4 px-4 text-center">
        <p className="text-gray-600 max-w-md">{error}</p>
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#002868] text-white py-5 print:hidden">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Agent Identification</p>
            <h1 className="text-xl font-bold">Colorado Bail Enforcement Agent ID</h1>
          </div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10">
            <ChevronLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Controls */}
        <div className="flex justify-center gap-3 mb-8 print:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFlipped(!flipped)}
            data-testid="flip-card-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {flipped ? 'Show Front' : 'Show Back'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-photo-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : data.photo ? 'Change Photo' : 'Upload Photo'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhotoUpload}
          />
        </div>

        {/* Single card display (flippable) — screen only */}
        <div className="flex justify-center print:hidden">
          {!flipped ? <CardFront data={data} /> : <CardBack data={data} />}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4 print:hidden">
          Lanyard badge size — print, cut, and attach to clip or lanyard
        </p>

        {/* Actions */}
        <div className="flex justify-center gap-3 mt-6 print:hidden">
          <Button variant="outline" size="sm" onClick={() => handleDownload('front')} data-testid="download-front-btn">
            <Download className="w-4 h-4 mr-2" /> Front
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleDownload('back')} data-testid="download-back-btn">
            <Download className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print (Duplex)
          </Button>
        </div>

        {/* Print instructions */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 print:hidden">
          <p className="text-sm font-semibold text-gray-800 mb-2">Printing Instructions</p>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li><strong>Duplex printer:</strong> Click "Print" — select "Print on both sides" or "Two-sided" in your printer settings. Front and back will auto-align.</li>
            <li><strong>Single-sided printer:</strong> Print page 1 (front), flip the paper, then print page 2 (back).</li>
            <li>Cut along the crop marks, laminate, and attach to lanyard or clip.</li>
          </ol>
        </div>

        {/* Preview - both sides (screen only) */}
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-8 print:hidden">
          <div>
            <p className="text-xs text-gray-400 mb-2 text-center">Front</p>
            <CardFront data={data} />
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-2 text-center">Back</p>
            <CardBack data={data} />
          </div>
        </div>

        {/* Print layout — front on page 1, back on page 2 */}
        <div className="hidden print:block">
          {/* Page 1: Front */}
          <div className="print-page flex flex-col items-center justify-center" style={{ pageBreakAfter: 'always', minHeight: '100vh' }}>
            <p className="text-xs text-gray-500 mb-3 font-semibold">FRONT — Cut along border</p>
            <CardFront data={data} />
            <p className="text-[10px] text-gray-400 mt-3">Colorado Bail Enforcement Agency LLC — Agent ID</p>
          </div>
          {/* Page 2: Back */}
          <div className="print-page flex flex-col items-center justify-center" style={{ minHeight: '100vh' }}>
            <p className="text-xs text-gray-500 mb-3 font-semibold">BACK — Cut along border, align with front</p>
            <CardBack data={data} />
            <p className="text-[10px] text-gray-400 mt-3">Colorado Bail Enforcement Agency LLC — Agent ID</p>
          </div>
        </div>
      </main>
    </div>
  );
}
